import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About CrowdRounds
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            We're building the validation infrastructure that helps founders prove product-market fit and investors make confident decisions
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Every year, thousands of promising apps fail — not because the idea was bad, but because founders didn't have the data to validate product-market fit before launch.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                CrowdRounds exists to change that. We provide founders with investor-grade validation data through real user testing, helping them identify and fix critical issues before they become expensive problems.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our CrownScore system gives investors a standardized, comparable metric to evaluate app readiness — reducing risk and accelerating funding decisions.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-12">
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Founded on Experience</h3>
                    <p className="text-gray-700">Built by founders who've been through the validation struggle firsthand</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Tester-First Approach</h3>
                    <p className="text-gray-700">Our verified tester network ensures quality, consistent feedback</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-bar-chart-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Data-Driven Results</h3>
                    <p className="text-gray-700">Every recommendation backed by real user behavior and metrics</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Our Impact</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">500+</div>
              <div className="text-xl text-slate-300">Apps Validated</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">10,000+</div>
              <div className="text-xl text-slate-300">Issues Identified</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">95%</div>
              <div className="text-xl text-slate-300">Founder Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">$50M+</div>
              <div className="text-xl text-slate-300">Funding Enabled</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-16">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-600 transition-all">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-blue-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Integrity First</h3>
              <p className="text-gray-700 leading-relaxed">
                We provide honest, unbiased validation data. No sugar-coating, no false positives — just the truth founders and investors need to make informed decisions.
              </p>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-purple-600 transition-all">
              <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-rocket-line text-purple-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Founder Success</h3>
              <p className="text-gray-700 leading-relaxed">
                Every test, every metric, every recommendation is designed to help founders build better products and achieve their vision faster.
              </p>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-teal-600 transition-all">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-teal-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Continuous Innovation</h3>
              <p className="text-gray-700 leading-relaxed">
                We're constantly improving our methodology, expanding our tester network, and developing new tools to deliver better validation insights.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We're Different */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-16">How We're Different</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-red-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Traditional User Testing</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                      <span>Random testers with no vetting</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                      <span>Subjective feedback without metrics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                      <span>No standardized scoring system</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                      <span>Results not investor-ready</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl p-8 shadow-lg text-white">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">CrowdRounds Validation</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-circle-line text-teal-300 mt-1 flex-shrink-0"></i>
                      <span>Verified testers matched to your app category</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-circle-line text-teal-300 mt-1 flex-shrink-0"></i>
                      <span>Quantified metrics + qualitative insights</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-circle-line text-teal-300 mt-1 flex-shrink-0"></i>
                      <span>CrownScore for investor comparability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-circle-line text-teal-300 mt-1 flex-shrink-0"></i>
                      <span>Investor-grade validation reports</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Ready to Validate Your App?</h2>
          <p className="text-xl text-gray-700 mb-8">
            Join hundreds of founders who've used CrowdRounds to prove product-market fit and secure funding
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a
              href="/client-portal/create-round"
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all whitespace-nowrap cursor-pointer"
            >
              Start Your First Test
            </a>
            <a
              href="/sample-report"
              className="px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-blue-600 hover:text-blue-600 transition-all whitespace-nowrap cursor-pointer"
            >
              View Sample Report
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
